import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { Modal, Input, Select, Button } from '../ui';
import { providerProfileApi, ApiError } from '../../api';
import type { ProviderLocation } from '../../types/provider';

// Form validation schema
const locationSchema = z.object({
	address: z.string().min(1, 'Street address is required'),
	city: z.string().min(1, 'City is required'),
	state: z.string().min(1, 'State is required'),
	zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Invalid zip code format (e.g., 12345)'),
	phone: z.string().optional(),
	email: z.string().email('Invalid email format').optional().or(z.literal('')),
	primary: z.boolean(),
	active: z.boolean(),
});

type LocationFormData = z.infer<typeof locationSchema>;

// US States list
const US_STATES = [
	{ value: 'AL', label: 'Alabama' },
	{ value: 'AK', label: 'Alaska' },
	{ value: 'AZ', label: 'Arizona' },
	{ value: 'AR', label: 'Arkansas' },
	{ value: 'CA', label: 'California' },
	{ value: 'CO', label: 'Colorado' },
	{ value: 'CT', label: 'Connecticut' },
	{ value: 'DE', label: 'Delaware' },
	{ value: 'FL', label: 'Florida' },
	{ value: 'GA', label: 'Georgia' },
	{ value: 'HI', label: 'Hawaii' },
	{ value: 'ID', label: 'Idaho' },
	{ value: 'IL', label: 'Illinois' },
	{ value: 'IN', label: 'Indiana' },
	{ value: 'IA', label: 'Iowa' },
	{ value: 'KS', label: 'Kansas' },
	{ value: 'KY', label: 'Kentucky' },
	{ value: 'LA', label: 'Louisiana' },
	{ value: 'ME', label: 'Maine' },
	{ value: 'MD', label: 'Maryland' },
	{ value: 'MA', label: 'Massachusetts' },
	{ value: 'MI', label: 'Michigan' },
	{ value: 'MN', label: 'Minnesota' },
	{ value: 'MS', label: 'Mississippi' },
	{ value: 'MO', label: 'Missouri' },
	{ value: 'MT', label: 'Montana' },
	{ value: 'NE', label: 'Nebraska' },
	{ value: 'NV', label: 'Nevada' },
	{ value: 'NH', label: 'New Hampshire' },
	{ value: 'NJ', label: 'New Jersey' },
	{ value: 'NM', label: 'New Mexico' },
	{ value: 'NY', label: 'New York' },
	{ value: 'NC', label: 'North Carolina' },
	{ value: 'ND', label: 'North Dakota' },
	{ value: 'OH', label: 'Ohio' },
	{ value: 'OK', label: 'Oklahoma' },
	{ value: 'OR', label: 'Oregon' },
	{ value: 'PA', label: 'Pennsylvania' },
	{ value: 'RI', label: 'Rhode Island' },
	{ value: 'SC', label: 'South Carolina' },
	{ value: 'SD', label: 'South Dakota' },
	{ value: 'TN', label: 'Tennessee' },
	{ value: 'TX', label: 'Texas' },
	{ value: 'UT', label: 'Utah' },
	{ value: 'VT', label: 'Vermont' },
	{ value: 'VA', label: 'Virginia' },
	{ value: 'WA', label: 'Washington' },
	{ value: 'WV', label: 'West Virginia' },
	{ value: 'WI', label: 'Wisconsin' },
	{ value: 'WY', label: 'Wyoming' },
];

export interface LocationModalProps {
	isOpen: boolean;
	onClose: () => void;
	/** Location to edit - if not provided, modal is in "Add" mode */
	location?: ProviderLocation | null;
	/** Called on successful save */
	onSuccess?: () => void;
}

const LocationModal = ({ isOpen, onClose, location, onSuccess }: LocationModalProps) => {
	const isEditMode = !!location;

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<LocationFormData>({
		resolver: zodResolver(locationSchema),
		defaultValues: {
			address: '',
			city: '',
			state: '',
			zipCode: '',
			phone: '',
			email: '',
			primary: false,
			active: true,
		},
	});

	// Reset form when modal opens/closes or location changes
	useEffect(() => {
		if (isOpen) {
			if (location) {
				reset({
					address: location.address || '',
					city: location.city || '',
					state: location.state || '',
					zipCode: location.zipCode || '',
					phone: location.phone || '',
					email: location.email || '',
					primary: location.primary || false,
					active: location.active ?? true,
				});
			} else {
				reset({
					address: '',
					city: '',
					state: '',
					zipCode: '',
					phone: '',
					email: '',
					primary: false,
					active: true,
				});
			}
		}
	}, [isOpen, location, reset]);

	const onSubmit = async (data: LocationFormData) => {
		const payload = {
			locationName: '',
			address: data.address,
			city: data.city,
			state: data.state,
			zipCode: data.zipCode,
			phone: data.phone || '',
			email: data.email || '',
			active: data.active,
			primary: data.primary,
		};

		try {
			if (isEditMode) {
				await providerProfileApi.updateLocation(location!.id, payload);
			} else {
				await providerProfileApi.addLocation(payload);
			}

			toast.success(
				isEditMode ? 'Location updated successfully!' : 'Location added successfully!'
			);
			onSuccess?.();
			onClose();
		} catch (error) {
			const apiError = error as ApiError;
			toast.error(
				apiError.message ||
					(isEditMode ? 'Failed to update location' : 'Failed to add location')
			);
		}
	};

	const handleDelete = async () => {
		if (!location) return;

		if (!window.confirm('Are you sure you want to delete this location?')) {
			return;
		}

		try {
			await providerProfileApi.deleteLocation(location.id);

			toast.success('Location deleted successfully!');
			onSuccess?.();
			onClose();
		} catch (error) {
			const apiError = error as ApiError;
			toast.error(apiError.message || 'Failed to delete location');
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={isEditMode ? 'Edit Location' : 'Add New Location'}
			size="lg"
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				{/* Header Icon */}
				<div className="flex items-center gap-3 mb-6">
					<div className="w-12 h-12 rounded-xl flex items-center justify-center text-indigo-600 dark:text-indigo-400 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30">
						<MapPinIcon className="w-6 h-6" />
					</div>
					<div>
						<p className="text-sm text-gray-500 dark:text-gray-400">
							{isEditMode
								? 'Update the details for this location'
								: 'Enter the details for a new salon location'}
						</p>
					</div>
				</div>

				{/* Street Address - Full Width */}
				<div className="mb-4">
					<Input
						label="Street Address"
						placeholder="123 Main Street"
						error={errors.address?.message}
						required
						disabled={isSubmitting}
						{...register('address')}
					/>
				</div>

				{/* City, State, Zip - Row */}
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
					<Input
						label="City"
						placeholder="Brooklyn"
						error={errors.city?.message}
						required
						disabled={isSubmitting}
						{...register('city')}
					/>
					<Select
						label="State"
						error={errors.state?.message}
						required
						disabled={isSubmitting}
						{...register('state')}
					>
						<option value="">Select State</option>
						{US_STATES.map(state => (
							<option key={state.value} value={state.value}>
								{state.label}
							</option>
						))}
					</Select>
					<Input
						label="Zip Code"
						placeholder="11201"
						error={errors.zipCode?.message}
						required
						disabled={isSubmitting}
						{...register('zipCode')}
					/>
				</div>

				{/* Phone, Email - Row */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
					<Input
						type="tel"
						label="Phone"
						placeholder="(555) 123-4567"
						error={errors.phone?.message}
						disabled={isSubmitting}
						{...register('phone')}
					/>
					<Input
						type="email"
						label="Email"
						placeholder="salon@email.com"
						error={errors.email?.message}
						disabled={isSubmitting}
						{...register('email')}
					/>
				</div>

				{/* Primary & Status - Row */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
					{/* Primary Checkbox */}
					<div className="flex items-center gap-3">
						<input
							type="checkbox"
							id="primary"
							className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
							disabled={isSubmitting}
							{...register('primary')}
						/>
						<label
							htmlFor="primary"
							className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
						>
							Set as Primary Location
						</label>
					</div>

					{/* Status */}
					<Select
						label="Status"
						error={errors.active?.message}
						disabled={isSubmitting}
						{...register('active', {
							setValueAs: (value: string) => value === 'true',
						})}
					>
						<option value="true">Active</option>
						<option value="false">Inactive</option>
					</Select>
				</div>

				{/* Action Buttons */}
				<div className="flex flex-col-reverse sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
					{/* Delete button - only in edit mode, hidden for primary locations */}
					{isEditMode && !location?.primary && (
						<Button
							type="button"
							variant="danger"
							onClick={handleDelete}
							disabled={isSubmitting}
							className="sm:mr-auto"
						>
							Delete Location
						</Button>
					)}

					<div className="flex gap-3 sm:ml-auto">
						<Button
							type="button"
							variant="secondary"
							onClick={onClose}
							disabled={isSubmitting}
						>
							Cancel
						</Button>
						<Button type="submit" variant="primary" isLoading={isSubmitting}>
							{isSubmitting
								? isEditMode
									? 'Updating...'
									: 'Adding...'
								: isEditMode
									? 'Update Location'
									: 'Add Location'}
						</Button>
					</div>
				</div>
			</form>
		</Modal>
	);
};

export default LocationModal;
